import React, { useState, useEffect } from 'react';
import { FiRefreshCw } from 'react-icons/fi';
import { useModal } from '../hooks/useModal';
import Modal from './Modal';

/**
 * Refresh button component that only appears for installed PWA (standalone mode)
 * Allows users to manually clear cache and refresh to get latest version
 */
export default function CacheRefreshButton() {
  const { modal, showModal, hideModal } = useModal();
  const [isStandalone, setIsStandalone] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    // Check if app is running in standalone mode (installed on home screen)
    const checkStandalone = () => {
      // Method 1: Check window.matchMedia for standalone display mode
      if (window.matchMedia) {
        const standaloneMatch = window.matchMedia('(display-mode: standalone)');
        if (standaloneMatch && standaloneMatch.matches) {
          console.log('[CacheRefreshButton] Standalone detected: display-mode: standalone');
          return true;
        }
      }
      
      // Method 2: Check for iOS standalone mode
      if (window.navigator && window.navigator.standalone === true) {
        console.log('[CacheRefreshButton] Standalone detected: iOS navigator.standalone');
        return true;
      }
      
      // Method 3: Check if launched from home screen (Android - fullscreen mode)
      if (window.matchMedia) {
        const fullscreenMatch = window.matchMedia('(display-mode: fullscreen)');
        if (fullscreenMatch && fullscreenMatch.matches) {
          console.log('[CacheRefreshButton] Standalone detected: display-mode: fullscreen');
          return true;
        }
      }
      
      // Method 4: Check if window is in standalone mode (some browsers)
      if (window.matchMedia) {
        const minUiMatch = window.matchMedia('(display-mode: minimal-ui)');
        if (minUiMatch && minUiMatch.matches && !window.matchMedia('(display-mode: browser)').matches) {
          console.log('[CacheRefreshButton] Standalone detected: display-mode: minimal-ui');
          return true;
        }
      }
      
      // Method 5: Check if running in PWA mode by checking if there's no browser chrome
      // This is a fallback for cases where display-mode might not be set correctly
      const hasNoBrowserChrome = 
        !window.matchMedia('(display-mode: browser)').matches &&
        (window.matchMedia('(display-mode: standalone)').matches ||
         window.matchMedia('(display-mode: fullscreen)').matches ||
         window.matchMedia('(display-mode: minimal-ui)').matches);
      
      if (hasNoBrowserChrome) {
        console.log('[CacheRefreshButton] Standalone detected: no browser chrome');
        return true;
      }
      
      // Debug: Log all detection attempts
      console.log('[CacheRefreshButton] Standalone check results:', {
        'display-mode: standalone': window.matchMedia ? window.matchMedia('(display-mode: standalone)').matches : 'N/A',
        'display-mode: fullscreen': window.matchMedia ? window.matchMedia('(display-mode: fullscreen)').matches : 'N/A',
        'display-mode: browser': window.matchMedia ? window.matchMedia('(display-mode: browser)').matches : 'N/A',
        'navigator.standalone': window.navigator ? window.navigator.standalone : 'N/A',
        'userAgent': navigator.userAgent
      });
      
      return false;
    };

    // Check immediately
    const standalone = checkStandalone();
    setIsStandalone(standalone);
    
    // Also listen for changes in display mode (in case it changes)
    if (window.matchMedia) {
      const mediaQueries = [
        window.matchMedia('(display-mode: standalone)'),
        window.matchMedia('(display-mode: fullscreen)'),
        window.matchMedia('(display-mode: minimal-ui)')
      ];
      
      const handleChange = () => {
        const newStandalone = checkStandalone();
        setIsStandalone(newStandalone);
      };
      
      mediaQueries.forEach(mq => {
        if (mq && mq.addEventListener) {
          mq.addEventListener('change', handleChange);
        } else if (mq && mq.addListener) {
          // Fallback for older browsers
          mq.addListener(handleChange);
        }
      });
      
      return () => {
        mediaQueries.forEach(mq => {
          if (mq && mq.removeEventListener) {
            mq.removeEventListener('change', handleChange);
          } else if (mq && mq.removeListener) {
            mq.removeListener(handleChange);
          }
        });
      };
    }
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    try {
      // First, fetch the current version from manifest before clearing
      let currentVersion = '1.0.0';
      try {
        const response = await fetch('/manifest.json');
        if (response.ok) {
          const manifest = await response.json();
          currentVersion = manifest.version || '1.0.0';
          console.log('[CacheRefresh] Current version before refresh:', currentVersion);
        }
      } catch (error) {
        console.warn('[CacheRefresh] Could not fetch manifest version:', error);
      }

      // Store flag indicating manual refresh was initiated with current version
      // This will be checked after refresh to prevent showing popup if version unchanged
      localStorage.setItem('manual_refresh_version', currentVersion);
      localStorage.setItem('manual_refresh_flag', 'true');
      console.log('[CacheRefresh] Set manual refresh flag with version:', currentVersion);

      // Clear all caches
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        );
        console.log('[CacheRefresh] Cleared all caches:', cacheNames);
      }

      // Unregister service worker if it exists
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(
          registrations.map(registration => registration.unregister())
        );
        console.log('[CacheRefresh] Unregistered service workers');
      }
      
      // Clear sessionStorage
      sessionStorage.clear();

      // Small delay to show feedback, then reload
      setTimeout(() => {
        window.location.reload(true);
      }, 500);
    } catch (error) {
      console.error('[CacheRefresh] Error clearing cache:', error);
      // Even if clearing fails, still reload
      window.location.reload(true);
    }
  };

  const handleRefreshClick = () => {
    showModal({
      title: 'Refresh App',
      message: 'This will clear the cache and reload the app to get the latest version. Continue?',
      type: 'info',
      onConfirm: handleRefresh,
      confirmText: 'Refresh',
      cancelText: 'Cancel',
      showCancel: true
    });
  };

  // Debug: Always log the state
  console.log('[CacheRefreshButton] isStandalone:', isStandalone);

  // TEMPORARY: For testing - check URL parameter to force show button
  const urlParams = new URLSearchParams(window.location.search);
  const forceShow = urlParams.get('showRefresh') === 'true';
  
  // Only show button in standalone mode (or if forced for testing)
  if (!isStandalone && !forceShow) {
    console.log('[CacheRefreshButton] Not showing button - not in standalone mode');
    return null;
  }

  if (forceShow) {
    console.log('[CacheRefreshButton] Force showing button for testing');
  } else {
    console.log('[CacheRefreshButton] Rendering refresh button - standalone mode detected');
  }

  return (
    <>
      <button
        onClick={handleRefreshClick}
        disabled={isRefreshing}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: isRefreshing 
            ? 'linear-gradient(135deg, #94a3b8 0%, #64748b 100%)'
            : forceShow
            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' // Red for test mode
            : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
          color: 'white',
          border: 'none',
          cursor: isRefreshing ? 'not-allowed' : 'pointer',
          boxShadow: forceShow 
            ? '0 4px 20px rgba(239, 68, 68, 0.6)' // More prominent shadow for test mode
            : '0 4px 12px rgba(139, 92, 246, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 99999, // Very high z-index to ensure it's on top
          transition: 'all 0.3s ease',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          outline: 'none'
        }}
        onMouseEnter={(e) => {
          if (!isRefreshing) {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 6px 16px rgba(139, 92, 246, 0.5)';
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(139, 92, 246, 0.4)';
        }}
        title="Refresh app and clear cache"
        aria-label="Refresh app"
      >
        {isRefreshing ? (
          <span 
            className="loading-elegant" 
            style={{ 
              borderTopColor: 'white',
              width: '24px',
              height: '24px'
            }}
          />
        ) : (
          <FiRefreshCw size={24} />
        )}
      </button>
      <Modal {...modal} onClose={hideModal} />
    </>
  );
}

